import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { MapPin, Clock, Users, Trophy, Download, Eye } from "lucide-react";

const POSTERS = [
  {
    slug: "basket-programme",
    label: "Basket 3x3 — Poules & phase finale",
    url: "https://customer-assets-0z36b82j.emergentagent.net/job_festurban-live/artifacts/vqvtr679_WhatsApp%20Image%202026-08-28%20at%2000.14.52.jpeg",
  },
  {
    slug: "basket-phase-finale",
    label: "Basket 3x3 — Programme du samedi",
    url: "https://customer-assets-0z36b82j.emergentagent.net/job_festurban-live/artifacts/w5ewhjzt_WhatsApp%20Image%202026-08-28%20at%2000.14.52%20%281%29.jpeg",
  },
  {
    slug: "foot-programme",
    label: "Foot 5v5 — Programme du dimanche",
    url: "https://customer-assets-0z36b82j.emergentagent.net/job_festurban-live/artifacts/z2m33569_WhatsApp%20Image%202026-08-28%20at%2000.15.11.jpeg",
  },
];

const downloadPoster = async (p) => {
  const res = await fetch(p.url);
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `urbanfest-${p.slug}.jpg`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const DAY_META = {
  samedi: {
    title: "Samedi 29 août",
    discipline: "Tournoi Basket 3x3",
    venue: "Gymnase Rivoli — La Roche-sur-Yon",
    facts: [
      { icon: Users, label: "12 équipes · 2 terrains" },
      { icon: Clock, label: "Poules : 10 min · Finales : 12 min (+1 TM 30s/équipe)" },
      { icon: Trophy, label: "3 poules de 4 → tableau final à 8 équipes" },
    ],
    accent: "#FF6B1A",
  },
  dimanche: {
    title: "Dimanche 30 août",
    discipline: "Tournoi Foot 5v5",
    venue: "Quartier Jean-Yole — La Roche-sur-Yon",
    facts: [
      { icon: Users, label: "16 équipes · championnat unique · 4 matchs par équipe" },
      { icon: Clock, label: "Matchs de 6 min · 5 min entre les matchs · départ 10h00" },
      { icon: Trophy, label: "Victoire 3 pts · Nul 1 pt · Défaite 0 pt — les 8 premiers en quarts" },
    ],
    accent: "#FF3B30",
  },
};

export default function Programme() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    api.get("/info").then((r) => setInfo(r.data));
  }, []);

  if (!info) return <div className="py-32 text-center text-white/60">Chargement…</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-20" data-testid="programme-page">
      <p className="text-xs tracking-[0.3em] uppercase text-[#FF6B1A] mb-4">Programme officiel</p>
      <h1 className="font-display text-5xl sm:text-7xl leading-none mb-4">LE PLANNING</h1>
      <p className="text-white/60 max-w-2xl mb-12 text-lg">
        Deux journées millimétrées : le Basket 3x3 le samedi au Gymnase Rivoli, le Foot 5v5 le dimanche à Jean-Yole. Prépare-toi à tout vivre.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <DayBlock meta={DAY_META.samedi} items={info.schedule.samedi} dayKey="samedi" />
        <DayBlock meta={DAY_META.dimanche} items={info.schedule.dimanche} dayKey="dimanche" />
      </div>

      <div className="mt-20" data-testid="posters-section">
        <p className="text-xs tracking-[0.3em] uppercase text-[#FF6B1A] mb-4">À partager</p>
        <h2 className="font-display text-4xl sm:text-5xl leading-none mb-8">LES AFFICHES OFFICIELLES</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {POSTERS.map((p) => (
            <div key={p.slug} className="uf-card overflow-hidden group" data-testid={`poster-${p.slug}`}>
              <a href={p.url} target="_blank" rel="noopener noreferrer" data-testid={`poster-view-${p.slug}`}>
                <img
                  src={p.url}
                  alt={p.label}
                  loading="lazy"
                  className="w-full aspect-[3/4] object-cover object-top group-hover:scale-[1.02] transition-transform duration-300"
                />
              </a>
              <div className="p-4 flex items-center gap-3">
                <p className="flex-1 text-sm font-bold leading-tight">{p.label}</p>
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 border border-white/20 rounded-full hover:border-[#FF6B1A] hover:text-[#FF6B1A] transition-colors"
                  title="Voir en grand"
                >
                  <Eye className="w-4 h-4" />
                </a>
                <button
                  onClick={() => downloadPoster(p)}
                  className="p-2 bg-[#FF6B1A] text-black rounded-full hover:brightness-110 transition-all"
                  title="Télécharger"
                  data-testid={`poster-download-${p.slug}`}
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DayBlock({ meta, items, dayKey }) {
  return (
    <div className="uf-card p-6 sm:p-8" data-testid={`day-${dayKey}`}>
      <div className="flex items-center gap-3 mb-3">
        <span className="w-3 h-10" style={{ background: meta.accent }} />
        <div>
          <h2 className="font-display text-3xl sm:text-4xl leading-none">{meta.title}</h2>
          <p className="font-display text-xl sm:text-2xl" style={{ color: meta.accent }}>{meta.discipline}</p>
        </div>
      </div>
      <p className="flex items-center gap-2 text-white/60 text-sm mb-5" data-testid={`venue-${dayKey}`}>
        <MapPin className="w-4 h-4" style={{ color: meta.accent }} /> {meta.venue}
      </p>
      <div className="space-y-2 mb-8">
        {meta.facts.map((f, i) => (
          <p key={i} className="flex items-start gap-2 text-xs uppercase tracking-wider text-white/70">
            <f.icon className="w-4 h-4 shrink-0 mt-0.5" style={{ color: meta.accent }} />
            {f.label}
          </p>
        ))}
      </div>
      <ol className="space-y-5">
        {items.map((it, i) => (
          <li key={i} className="grid grid-cols-[auto_1fr] gap-4 sm:gap-6 pb-5 border-b border-white/10 last:border-0">
            <span
              className="font-mono text-sm tracking-wider whitespace-nowrap pt-1"
              style={{ color: meta.accent }}
            >
              {it.time}
            </span>
            <span className="text-white/90">{it.activity}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}
